import { XGrid } from '@AMIEWEB/Common';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { formatMultiSortColumns, formatMultiSortXGridColumns } from '@AMIEWEB/GlobalSearch/helper';
import { GridPinnedColumns, GridSortModel } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserGridPreference, selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GridColumns as XGridColumns, GridColumnVisibilityModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { globalActions } from 'app/ApplicationRoot/Global.redux';

export const defaultPinnedColumns = {
  left: [],
  right: [],
};

export const XGridWithPreference = props => {
  const dispatch = useDispatch();
  const gridPreferences = useSelector(selectUserGridPreference).find(item => item.id === props.gridTag);
  const gridState = useSelector(selectGridState);
  const userPreference = useSelector(selectUserPreference);
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumns>(defaultPinnedColumns);
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(props.columnVisibility || {}),
  );

  //**Effects START*/
  useEffect(() => {
    if (gridPreferences?.value?.multiSortedColumns?.length > 0) {
      setCurrentSortModel(getSortModelPreferences(gridPreferences?.value?.multiSortedColumns));
    }
  }, [gridPreferences]);
  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(props.gridTag));
      determineVisibilityModel();
      determineColumn();
      determinePinnedColumns();
    }
  }, [userPreference?.loaded, gridPreferences]);
  const getSortModelPreferences = (sortModel: any[]) => {
    const columnPreferences = gridPreferences?.value;
    if (
      columnPreferences &&
      columnPreferences?.multiSortedColumns &&
      Array.isArray(columnPreferences?.multiSortedColumns)
    ) {
      return formatMultiSortXGridColumns(columnPreferences?.multiSortedColumns);
    }
    return sortModel;
  };

  const determineVisibilityModel = () => {
    const currentVisibilityModel = getVisibilityPreferences(props.columnVisibility);
    setCurrentColumnVisibilityModel(currentVisibilityModel);
  };
  const determinePinnedColumns = () => {
    const pinnedColumn = gridPreferences?.value?.pinnedColumns || defaultPinnedColumns;
    setPinnedColumns(pinnedColumn);
  };
  const determineColumn = () => {
    const currentColumn = getColumnPreferences(props.columns);
    props.setColumns(currentColumn);
  };
  //Get the grid columns from preferences if available
  const getColumnPreferences = (cols: any[]) => {
    try {
      const columnPreferences = gridPreferences?.value;
      const filterSetColumns = gridPreferences?.value?.filterAttributes?.filterSet?.find(
        item => item.name === gridPreferences?.value?.selectedFilter?.name,
      )?.columns;
      if (columnPreferences && (columnPreferences.columns || filterSetColumns)) {
        const newColumns = cols.map(column => {
          const foundIndex = (filterSetColumns ?? columnPreferences.columns).findIndex(
            item => item?.field === column?.field,
          );
          if (foundIndex !== -1) {
            return {
              ...column,
              ...(filterSetColumns ?? columnPreferences.columns)[foundIndex],
            };
          } else {
            return column;
          }
        });
        return newColumns.sort((a, b) => {
          const filterSetColumnsIndexA = (filterSetColumns ?? columnPreferences.columns).findIndex(
            i => i.field === a.field,
          );
          const filterSetColumnsIndexB = (filterSetColumns ?? columnPreferences.columns).findIndex(
            i => i.field === b.field,
          );

          const indexA = filterSetColumnsIndexA === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexA;
          const indexB = filterSetColumnsIndexB === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexB;

          return indexA - indexB;
        });
      }
      return cols;
    } catch (err) {
      console.error('Error in getColumnPreferences:', err);
      return cols;
    }
  };

  const getVisibilityPreferences = (cols: any) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences && columnPreferences?.columnVisibilityModel) {
      return columnPreferences?.columnVisibilityModel;
    }
    return cols;
  };

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      setPinnedColumns(updatedPinnedColumns);
      const preferenceData = {
        id: props.gridTag,
        value: {
          ...(gridPreferences?.value || {}),
          pinnedColumns: updatedPinnedColumns,
        },
      };
      dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    },
    [dispatch, props.gridTag, gridPreferences?.value, setPinnedColumns],
  );

  //**SORT MODEL CHANGE */
  const onSortModelChanged = (model: GridSortModel) => {
    saveSortModel(model);
    setCurrentSortModel(model);
  };
  const saveSortModel = React.useCallback(
    sortModel => {
      const multiSortedColumns = sortModel?.length > 0 ? formatMultiSortColumns(sortModel) : undefined;
      if (!_.isEqual(multiSortedColumns, gridState?.multiSortedColumns)) {
        const preferenceData = {
          id: props.gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            multiSortedColumns: multiSortedColumns,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridPreferences?.value, props.columns?.length, gridState?.multiSortedColumns, currentColumnVisibilityModel],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      field?: string;
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!props.columns || props.columns.length === 0 || !userPreference.loaded) return;
      const currentGridColumns = new GridColumns(gridColumns).columns;

      // Remove the automatic checkbox column (has no field name)
      if (currentGridColumns && currentGridColumns.length > 0 && !currentGridColumns[0].field)
        currentGridColumns.shift();

      const comparableColumns = new GridColumns(
        gridPreferences?.value?.columns,
        gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        const preferenceData = {
          id: props.gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [userPreference.loaded, gridPreferences?.value, dispatch],
  );
  //***RESET COLUMNS */
  const resetColumns = () => {
    //Reset grid attributes to default values
    props.setColumns(props.defaultColumns);
    setCurrentSortModel([]);
    setCurrentColumnVisibilityModel(props.defaultColumnVisibilityModel);
    setPinnedColumns(defaultPinnedColumns);

    const preferenceData = {
      id: props.gridTag,
      value: {
        ...(gridPreferences?.value || {}),
        columns: props.defaultColumns,
        columnVisibilityModel: props.defaultColumnVisibilityModel,
        pinnedColumns: defaultPinnedColumns,
        multiSortedColumns: [],
      },
    };
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    dispatch(
      globalActions.setSnackBar({
        message: 'Columns have been reset.',
        severity: 'success',
      }),
    );
  };
  return (
    <>
      <XGrid
        {...props}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        defaultSortModel={currentSortModel}
        sortModel={currentSortModel}
        onSortModelChange={onSortModelChanged}
        defaultColumnVisibilityModel={props.defaultColumnVisibilityModel}
        columnVisibilityModel={currentColumnVisibilityModel}
        onColumnVisibilityModelChange={(model, details) => {
          dispatch(gridStateActions.setColumnVisibilityModel(model));
          setCurrentColumnVisibilityModel(_.cloneDeep(model));
          const columns = props.apiRef?.current?.getAllColumns();
          savePreferences({
            field: undefined,
            columns: _.cloneDeep(columns),
            visibilityModel: _.cloneDeep(model),
          });
        }}
        onColumnOrderChange={(params, event, details, _columns) => {
          savePreferences({
            field: params.field,
            columns: _columns,
            visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? props.defaultColumnVisibilityModel),
          });
        }}
        onColumnResizeStop={_columns => {
          savePreferences({
            field: undefined,
            columns: _columns,
            visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? props.defaultColumnVisibilityModel),
          });
        }}
        toolbarProps={{
          resetColumns: resetColumns,
          ...props.toolbarProps,
        }}
      />
    </>
  );
};
